/* eslint-disable @typescript-eslint/no-misused-promises */
import { FormSelectGroup } from 'components/form/select';
import FormTextfield, { TextfieldModifier } from 'components/form/textfield';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  useContext, useEffect, useRef, useState,
} from 'react';
import FormCheckbox, { FormCheckboxModifier } from 'components/form/checkbox';
import { useTranslation } from 'react-i18next';
import { StoreContext } from '../../../models/import';
import { TooltipAlignEnum } from '../../tooltip';
import { getSmallMismatchValueInUserCurrency } from '../../../pages/overview/balanceStatusTooltip';

export interface BalanceCheckTableFilterProps { // implements TableFilterProps
  useLocalSearch: boolean;
  leftAlignedSearch: boolean;
  onFitlerCB: (e: string) => void;
  onGlobalFilterUpdate: (e: string) => void;
  // This cannot be null/undefined or the current implementation of the checkbox will bug out.
  ignoreSmallMismatchesProp: boolean;
  ignoreUserCurrencyMismatchesProp: boolean;
  onSmallMismatchUpdate: (e: boolean) => void;
  onUserCurrencyMismatchUpdate: (e: boolean) => void;

  onWarningsUpdate: (e: boolean) => void;
  onShowReportedBalanceUpdate: (e: boolean) => void;
  showWarningsProp: boolean;
  showReportedBalancesProp: boolean;

  disabled: boolean;
  tooltipAlignment: TooltipAlignEnum;
}

export default function BalanceCheckDetailsTableFilters(props: BalanceCheckTableFilterProps) {
  const {
    leftAlignedSearch,
    onFitlerCB,
    onSmallMismatchUpdate,
    onUserCurrencyMismatchUpdate,
    ignoreSmallMismatchesProp,
    ignoreUserCurrencyMismatchesProp,
  } = props;
  const { t } = useTranslation();

  const [zeroBalanceData, setZeroBalanceData] = useState<FormSelectGroup[] | null>(null);
  const [warningData, setWarningData] = useState<FormSelectGroup[] | null>(null);
  const prevFilterRef = useRef<string | null>(null);

  const { userSettings } = useContext(StoreContext);
  const smallMismatchValueInUserCurrency : string = getSmallMismatchValueInUserCurrency();

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      search: '',
      zeroBalance: true,
      warning: false,
      ignoreSmallMismatches: ignoreSmallMismatchesProp,
      ignoreUserCurrencyMismatches: ignoreUserCurrencyMismatchesProp,
    },
  });

  useEffect(() => {
    const subscription = formMethods.watch((data) => {
      let mismatchUpdate: boolean | undefined;
      let userCurrencyMismatchUpdate: boolean | undefined;
      const {
        zeroBalance, warning, ignoreSmallMismatches, ignoreUserCurrencyMismatches,
      } = data;
      const formatedData = {
        ...data,
        zeroBalance: zeroBalance ? zeroBalance as unknown as boolean : false,
        warning: warning ? warning as unknown as boolean : false,
        ignoreSmallMismatches: ignoreSmallMismatches
          ? ignoreSmallMismatches as unknown as boolean
          : false,
        ignoreUserCurrencyMismatches: ignoreUserCurrencyMismatches
          ? ignoreUserCurrencyMismatches as unknown as boolean
          : false,
        forceRefreshCache: false,
      };

      const filterString = (Object.keys(formatedData) as Array<keyof typeof formatedData>)
        .reduce((acc, key) => {
          const value = formatedData[key];

          if (value !== undefined && value !== '') {
            switch (key) {
              case 'search':
                acc.push(`filter[searchTerm]=${formatedData[key] as string}`);
                break;
              case 'zeroBalance':
                acc.push(`filter[hideZeroBalances]=${formatedData[key] ? '1' : '0'}`);
                break;
              case 'warning':
                acc.push(`filter[showOnlyWarnings]=${formatedData[key] ? '1' : '0'}`);
                break;
              case 'ignoreSmallMismatches':
                acc.push(`filter[ignoreSmallMismatches]=${formatedData[key] ? '1' : '0'}`);
                mismatchUpdate = formatedData[key];
                break;
              case 'ignoreUserCurrencyMismatches':
                acc.push(`filter[ignoreUserCurrencyMismatches]=${formatedData[key] ? '1' : '0'}`);
                userCurrencyMismatchUpdate = formatedData[key];
                break;
              case 'forceRefreshCache':
                acc.push('filter[forceRefreshCache]=0');
                break;
              default:
                break;
            }
          }

          return acc;
        }, [] as string[]).join('&');

      // no change in string
      if (prevFilterRef.current && prevFilterRef.current === filterString) return;

      prevFilterRef.current = filterString;
      onFitlerCB(filterString);
      if (mismatchUpdate !== undefined) {
        onSmallMismatchUpdate(mismatchUpdate);
      }
      if (userCurrencyMismatchUpdate !== undefined) {
        onUserCurrencyMismatchUpdate(userCurrencyMismatchUpdate);
      }
    });

    if (!zeroBalanceData && !warningData) {
      setZeroBalanceData([{ label: '', options: [] }]);
      setWarningData([{ label: '', options: [] }]);
      formMethods.setValue('zeroBalance', true, { shouldValidate: false });

      let validateOnce = true;
      // we should not call shouldValidate twice or we will have 2 network calls
      if (ignoreSmallMismatchesProp !== null && ignoreSmallMismatchesProp !== undefined) {
        // due to https://github.com/react-hook-form/react-hook-form/issues/2472 this needs to be the same as the value set in defaultValues
        // this forces a .watch update to send the correct network call.
        formMethods.setValue('ignoreSmallMismatches', ignoreSmallMismatchesProp, { shouldValidate: validateOnce });
        validateOnce = !validateOnce;
      }
      if (ignoreUserCurrencyMismatchesProp !== null && ignoreUserCurrencyMismatchesProp !== undefined) {
        // due to https://github.com/react-hook-form/react-hook-form/issues/2472 this needs to be the same as the value set in defaultValues
        // this forces a .watch update to send the correct network call.
        formMethods.setValue('ignoreUserCurrencyMismatches', ignoreUserCurrencyMismatchesProp, { shouldValidate: validateOnce });
      }
    }

    return () => {
      subscription.unsubscribe();
    };
  }, [formMethods.watch]);

  return (
    <FormProvider {...formMethods}>
      <form className="ct_form" onSubmit={(e) => e.preventDefault()}>
        <div className={`ct_tableFilter ${leftAlignedSearch ? '_left' : ''}`}>

        {leftAlignedSearch && (
          <div className="ct_tableFilter-header _inactive">
          <div className="ct_tableFilter-search _old">
            <FormTextfield
              name="search"
              options={{ maxLength: 40 }}
              placeholder={t('Search', { ns: 'overview' })}
              debounceMs={400}
              clearable
              modifiers={[TextfieldModifier.OVERVIEW]}
            />
          </div>
          </div>
        )}

          <div className="ct_tableFilter-ignoreUserCurrencyMismatches">
            <Controller
              control={formMethods.control}
              name="ignoreUserCurrencyMismatches"
              render={({ field: { name, value, onChange }, formState }) => (
                <FormCheckbox
                  name={name}
                  label={t(
                    'Ignore {{userCurrency}} values',
                    { 
                      userCurrency: userSettings.currency,
                      ns: 'overview' 
                    },
                  )}
                  defaultChecked={value}
                  modifiers={[FormCheckboxModifier.OLD]}
                  onChange={onChange}
                  tooltip={{
                    align: TooltipAlignEnum.RIGHT,
                    message: t(
                      'Mismatches in {{userCurrency}} won\'t be shown as warning',
                      { 
                        userCurrency: userSettings.currency,
                        ns: 'overview' 
                      },
                    ),
                  }}
                />
              )}
            />
          </div>

          <div className="ct_tableFilter-warning">
            <Controller
              control={formMethods.control}
              name="warning"
              render={({ field: { name, value, onChange }, formState }) => (
                <FormCheckbox
                  name={name}
                  label={t('Warnings', { ns: 'overview' })}
                  defaultChecked={value}
                  modifiers={[FormCheckboxModifier.OLD]}
                  onChange={onChange}
                  tooltip={{
                    align: TooltipAlignEnum.RIGHT,
                    message: t('No warnings will be shown at all', { ns: 'overview' }),
                  }}
                />
              )}
            />
          </div>

        {!leftAlignedSearch && (
          <div className="ct_tableFilter-header _inactive">
          <div className="ct_tableFilter-search _old">
            <FormTextfield
              name="search"
              options={{ maxLength: 40 }}
              placeholder={t('Search', { ns: 'overview' })}
              debounceMs={400}
              clearable
              modifiers={[TextfieldModifier.OVERVIEW]}
            />
          </div>
          </div>
        )}

        </div>
      </form>
    </FormProvider>
  );
}
